import React from "react";
import { TFunction } from "i18next";
import { toast, ToastOptions } from "react-toastify";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { uuid } from "uuidv4";
import { useDispatch } from "react-redux";

import { getData } from "../../../../../helpers/general";
import { buildFidelityPayload } from "../../helpers";
import { CESAR_LOYALTY } from "@constants/index";
import { AppDispatch } from "../../../../../redux/store";
import {
    setKey,
    setIsLoading,
} from "../../../../../redux/slices/fidelitySlice";

type Params = {
    row: FidelityRow;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    fetchGlobal: FetchGlobal;
    t: TFunction;
};

export function useActivateFidelity(params: Params) {
    const {
        row,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        fetchGlobal,
        t,
    } = params;
    const dispatch = useDispatch<AppDispatch>();

    return React.useCallback(async () => {
        toast.dismiss();
        dispatch(setIsLoading(true));

        let nextIsArchived = row.isArchived;
        if (
            row.fidelityChoiceIndex === CESAR_LOYALTY &&
            Array.isArray(row.isArchived)
        ) {
            const index = row.isArchived.findIndex(
                (shop: FidelityArchiveEntry) =>
                    shop.shopId === `${row.affectedShop}` ||
                    shop.shopId === row.affectedShop
            );
            if (index !== -1) {
                nextIsArchived = row.isArchived.map(
                    (shop: FidelityArchiveEntry, i: number) =>
                        i === index ? { ...shop, status: false } : shop
                );
            }
        }

        const payload = buildFidelityPayload(row, t);
        payload.isArchived =
            row.fidelityChoiceIndex !== CESAR_LOYALTY
                ? false
                : JSON.stringify(nextIsArchived);

        const body = {
            userId: userID,
            shopId: [row.shopId.toString()],
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser,
            isShop: false,
            data: { [row.Id]: payload },
            add: true,
        };

        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;

        try {
            const result = (await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            })) as { error: boolean };
            if (result.error) throw new Error("error");

            toast.success(
                t("The fidelity setting has been successfully unarchive"),
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                } as ToastOptions
            );
            dispatch(setKey(uuid()));
            await getData(
                franchiseID,
                uuidUser as string,
                row.shopId,
                fetchGlobal
            );
        } catch (err) {
            toast.error(t("There's an error"), {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            dispatch(setIsLoading(false));
        }
        // Why: `dispatch` stable (React-Redux). `fetchGlobal` recreated each render
        // by useFetchGlobal — listing it would defeat memoization without value.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row, userID, franchiseID, operatorID, uuidUser, t]);
}
